
// Copyright (c) Docugami, Inc. All rights reserved.

import { TokenRange } from "networking/api";
import { BoundsComponent } from "sdv/components/BoundsComponent";
import { getTokenRange } from "sdv/components/ControlComponent";
import { TokenRangeBox } from "sdv/components/TokenLinesComponent";
import { ComponentId } from "sdv/engine/Component";
import { Entity } from "sdv/engine/Entity";
import { getTokenRangesForPages, Page } from "sdv/Page";
import { Box, TokenIdToBox } from "store/shared_types";
import { TokenRangeUtils } from "utils/TokenRangeUtils";

export function clampTokenRangeToPages(fromTok: number, toTok: number, pages: Page[]): TokenRange | undefined {
    const pageRanges = getTokenRangesForPages(pages);
    // Filter out the ranges that are not intersecting at all
    const overlappingRanges = pageRanges.filter(pageRange => TokenRangeUtils.rangesOverlap({ fromTok: fromTok, toTok: toTok }, pageRange));
    if (!overlappingRanges?.length) {
        return;
    }

    fromTok = Math.max(Math.min(...overlappingRanges.map(x => x.fromTok)), fromTok);
    toTok = Math.min(Math.max(...overlappingRanges.map(x => x.toTok)), toTok);
    return {
        fromTok: fromTok,
        toTok: toTok
    }
}

export function createBoundsForTokenRange(fromTok: number, toTok: number, tokens: TokenIdToBox, structuralEntities: Entity[] = []): Box[] {
    let remainingRange: TokenRange = { fromTok: fromTok, toTok: toTok };
    const bounds: Box[] = [];
    for (const entity of structuralEntities) {
        const structuralRange = getTokenRange(entity);
        if (!!structuralRange && TokenRangeUtils.rangeIsContained(structuralRange, remainingRange)) {
            if (structuralRange.fromTok > remainingRange.fromTok) {
                // Add beginning range to bounds via token bounds
                const tokenBounds = createTokenRangeBoxes(remainingRange.fromTok, structuralRange.fromTok, tokens).map(x => x.box);
                bounds.push(...tokenBounds);
            }

            const structuralBounds = entity.getComponent<BoundsComponent>(ComponentId.Bounds)!;
            bounds.push(...structuralBounds.bounds.map(x => { return { ...x } }));

            remainingRange.fromTok = structuralRange.toTok;
        }
    }

    if (remainingRange.fromTok !== remainingRange.toTok) {
        const tokenBounds = createTokenRangeBoxes(remainingRange.fromTok, remainingRange.toTok, tokens).map(x => x.box);
        bounds.push(...tokenBounds);
    }

    return bounds;
}

export function createTokenRangeBoxes(fromTok: number, toTok: number, tokens: TokenIdToBox): TokenRangeBox[] {
    let tokenLines: TokenRangeBox[] = [];
    let rangeBox = getTokenBox(fromTok, tokens);
    let lineRange: TokenRange = { fromTok: fromTok, toTok: fromTok + 1 };
    let tokenId = fromTok + 1;

    // move past space tokens
    while (!rangeBox && tokenId < toTok) {
        rangeBox = getTokenBox(tokenId, tokens);
        tokenId++;
    }

    lineRange.toTok = tokenId;

    if (!!rangeBox) {
        for (; tokenId < toTok; tokenId++) {
            let tokenBox = getTokenBox(tokenId, tokens);
            if (!tokenBox) {
                continue;
            }

            if (tokenBox.right >= (rangeBox.right - 10) &&
                Math.max(tokenBox.top, rangeBox.top) < Math.min(tokenBox.bottom, rangeBox.bottom)) {
                // overlapping tokens, let's merge
                rangeBox.left = Math.min(rangeBox.left, tokenBox.left);
                rangeBox.top = Math.min(rangeBox.top, tokenBox.top);
                rangeBox.right = Math.max(rangeBox.right, tokenBox.right);
                rangeBox.bottom = Math.max(rangeBox.bottom, tokenBox.bottom);

                lineRange.toTok = tokenId + 1;
            }
            else {
                tokenLines.push({
                    tokenRange: { ...lineRange },
                    box: rangeBox
                });

                lineRange.fromTok = tokenId;
                lineRange.toTok = tokenId + 1;
                rangeBox = tokenBox;
            }
        }

        tokenLines.push({
            tokenRange: { ...lineRange },
            box: rangeBox
        });
    }

    return tokenLines;
}

export function sortEntitiesByTokenRangeAscending(structuralEntities: Entity[]) {
    structuralEntities.sort(ascendingTokenRangeSort);
    return structuralEntities;
}

function ascendingTokenRangeSort(e1: Entity, e2: Entity): number {
    const r1 = getTokenRange(e1);
    const r2 = getTokenRange(e2);
    if (r1 === null || r2 === null) {
        throw new Error("Cannot sort entity without token range.");
    }

    if (r1.fromTok < r2.fromTok) {
        return -1;
    }
    else if (r1.fromTok === r2.fromTok) {
        if (r1.toTok === r2.toTok) {
            return 0;
        }

        return r1.toTok > r2.toTok ? -1 : 1;
    }
    else {
        return 1;
    }
}

export function mergeGapsBetweenBoxes(boxes: Box[], gapSize: number): void {
    let prevBox: Box | null = null;
    for (const box of boxes) {
        if (!!prevBox) {
            if (box.top > prevBox.bottom && (box.top - prevBox.bottom) <= gapSize) {
                const diff = (box.top - prevBox.bottom) / 2.0;
                box.top -= Math.ceil(diff);
                prevBox.bottom += Math.floor(diff);
            }
        }

        prevBox = box;
    }
}

export function getTokenBox(token: number, tokens: TokenIdToBox): Box | null {
    const box = tokens[token];
    if (!!box) {
        return {...box};
    }

    return null;
}

export function clampAndCreateChunkBounds(fromTok: number, toTok: number, tokens: TokenIdToBox, pages: Page[], mergeGap: number = 10, structuralEntities: Entity[] = []): Box[] {
    if (!!pages.length) {
        const range = clampTokenRangeToPages(fromTok, toTok, pages);
        if (!range) {
            return [];
        }

        fromTok = range.fromTok;
        toTok = range.toTok;
    }

    const bounds = createBoundsForTokenRange(fromTok, toTok, tokens, structuralEntities);
    mergeGapsBetweenBoxes(bounds, mergeGap);

    return bounds;
}

export function mergeBoxesIntoContainerBoxes(boxes: Box[]): Box[] {
    const containerBoxes: Box[] = [];
    if (boxes.length === 0) {
        return containerBoxes;
    }

    let combined = { ...boxes[0] };
    for (let i = 1; i < boxes.length; i++) {
        const box = boxes[i];
        if (box.page === combined.page && box.bottom > combined.top && box.left < combined.right) {
            combined = {
                left: Math.min(box.left, combined.left),
                right: Math.max(box.right, combined.right),
                top: combined.top,
                bottom: box.bottom,
                page: combined.page
            }
        } else {
            containerBoxes.push(combined);
            let prevCombined = { ...combined }
            combined = { ...box }

            // TODOLC: NOT THE REAL ANSWER HERE ... QUICK HACK.  REALLY NEED TO TRACK ALL BOXES TO
            // LOOK FOR THESE CROSS PAGE COLUMN FLOWS AND MAKE WIDTHS CONSISTENT
            if (Math.min(box.right, prevCombined.right) > Math.max(box.left, prevCombined.left)) {
                combined.left = prevCombined.left;
                combined.right = prevCombined.right;
            }
        }
    }

    if (!!combined) {
        containerBoxes.push(combined);
    }

    return containerBoxes;
}
